// @ts-strict-ignore
import * as React from 'react';
import Button from 'antd/lib/button';
import Image from 'antd/lib/image';
import Popover from 'antd/lib/popover';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import { MessageFilled, MessageOutlined } from '@ant-design/icons';
import { ChecklistItemComment } from '../../types';
import { antGrey } from '../styles/globalStyles';
import { formatDateWithTimezone } from '../../timeHelpers';
import MarkdownComponent from '../HOCs/Markdown';
import { getChecklistAttachmentURLs } from '../../api';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectApp } from '../../selectors/selectors';
import RetryableChecklistAttachmentImage from './RetryableChecklistAttachmentImage';

interface ChecklistItemCommentButtonProps {
    id: string;
    comment?: ChecklistItemComment;
    isDisabled: boolean;
    onAddComment: (id: string) => void;
    effectiveTimezone: string;
    use24HourTimeFormat: boolean;
    orgId: string;
    appId: string;
}

const ChecklistItemCommentButton: React.FC<ChecklistItemCommentButtonProps> = (
    props: ChecklistItemCommentButtonProps,
) => {
    const { id, comment, isDisabled, onAddComment, effectiveTimezone, use24HourTimeFormat, orgId, appId } = props;
    const workflowId = useSelector((state: RootState) => selectApp(state, appId)?.workflowId);
    const hasComment = !!comment?.comment;
    const hasAttachments = comment?.attachments?.length > 0;
    const textColor = isDisabled ? antGrey.grey6 : antGrey.grey8;

    const auth = useSelector((state: RootState) => state.auth.authCredentials);
    const [imageUrls, setImageUrls] = React.useState<Record<string, string>>({});
    const [loadingUrls, setLoadingUrls] = React.useState(false);
    const [urlsLoaded, setUrlsLoaded] = React.useState(false);

    const loadAttachmentURLs = () => {
        if (urlsLoaded || !hasAttachments || !workflowId || !auth) return;
        const s3Keys = comment.attachments.map((att) => att.s3Key).filter(Boolean);
        if (s3Keys.length === 0) return;

        setLoadingUrls(true);
        getChecklistAttachmentURLs(orgId, workflowId, s3Keys, auth)
            .then((response) => {
                setImageUrls(response.data ?? {});
                setUrlsLoaded(true);
            })
            .catch(() => setUrlsLoaded(true))
            .finally(() => setLoadingUrls(false));
    };

    const button = (
        <Button
            icon={
                hasComment || hasAttachments ?
                    <MessageFilled style={{ color: antGrey.grey7 }} />
                :   <MessageOutlined style={{ color: antGrey.grey7 }} />
            }
            style={{ borderStyle: 'none', display: 'block' }}
            onClick={() => onAddComment(id)}
            disabled={isDisabled}
        />
    );

    if ((!hasComment && !hasAttachments) || isDisabled) {
        return <div>{button}</div>;
    }

    const visibleAttachments = comment?.attachments?.filter((att) => imageUrls[att.s3Key]) ?? [];

    return (
        <Popover
            title={null}
            onOpenChange={(open) => {
                if (open) loadAttachmentURLs();
            }}
            content={
                <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 400 }}>
                    {hasComment && <MarkdownComponent source={comment.comment} />}
                    {hasAttachments && (
                        <div style={{ marginTop: hasComment ? 8 : 0 }}>
                            {loadingUrls ?
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {comment.attachments.map((att) => (
                                        <div
                                            key={att.s3Key}
                                            style={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: 4,
                                                border: `1px solid ${antGrey.grey4}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: antGrey.grey2,
                                            }}
                                        >
                                            <Spin size="small" />
                                        </div>
                                    ))}
                                </div>
                            :   visibleAttachments.length > 0 && (
                                    <Image.PreviewGroup>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                            {visibleAttachments.map((att) => (
                                                <RetryableChecklistAttachmentImage
                                                    key={att.s3Key}
                                                    src={imageUrls[att.s3Key]}
                                                    alt={att.fileName}
                                                    width={60}
                                                    height={60}
                                                />
                                            ))}
                                        </div>
                                    </Image.PreviewGroup>
                                )
                            }
                        </div>
                    )}
                    <Text type="secondary" style={{ marginTop: 12, color: textColor }}>
                        Last updated by {comment.createdBy} on{' '}
                        {formatDateWithTimezone(comment.createdAt, 'short', effectiveTimezone, use24HourTimeFormat)}
                    </Text>
                </div>
            }
            placement="topRight"
            mouseEnterDelay={0.4}
        >
            <div>{button}</div>
        </Popover>
    );
};

export default ChecklistItemCommentButton;
